import React, { useState, useEffect } from 'react'
import axios from 'axios'
import DateRangeSelector, { Range } from '../components/DateRangeFilter'
import { format, subDays } from 'date-fns'
import { getServiceAbbreviation } from '../lib/serviceAbbreviations'
import { EmptyState } from '../components/EmptyState'

interface ComponentCost {
  component: string
  cost: number
  percentage: number
}

interface ServiceCost {
  service: string
  cost: number
  percentage: number
  components?: ComponentCost[]
  component?: string
  resource?: string
}

interface AnalysisFilters {
  service: string
  groupBy: 'service' | 'component' | 'resource'
  tags: Record<string, string>
}

interface AvailableTag {
  key: string
  values: string[]
  count: number
}

interface HierarchicalService {
  service: string
  cost: number
  percentage: number
  components?: HierarchicalComponent[]
}

interface HierarchicalComponent {
  component: string
  cost: number
  percentage: number
  resources?: HierarchicalResource[]
}

interface HierarchicalResource {
  resource: string
  cost: number
  percentage: number
}

export default function CostAnalysisPage() {
  const [serviceCosts, setServiceCosts] = useState<ServiceCost[]>([])
  const [hierarchicalData, setHierarchicalData] = useState<HierarchicalService[]>([])
  const [loading, setLoading] = useState(true)
  const [dateRange, setDateRange] = useState<Range>({
    start: subDays(new Date(), 29),
    end: new Date(),
    preset: 30
  })
  const [totalCost, setTotalCost] = useState(0)
  /** Org-wide period comparison from /summary; null when service/tag filters are applied */
  const [periodChangePercent, setPeriodChangePercent] = useState<number | null>(null)

  const [appliedFilters, setAppliedFilters] = useState<AnalysisFilters>({
    service: '',
    groupBy: 'service',
    tags: {}
  })

  const [pendingFilters, setPendingFilters] = useState<AnalysisFilters>({
    service: '',
    groupBy: 'service',
    tags: {}
  })

  const [expandedServices, setExpandedServices] = useState<Set<string>>(new Set())
  const [expandedComponents, setExpandedComponents] = useState<Set<string>>(new Set())
  const [availableServices, setAvailableServices] = useState<string[]>([])
  const [availableTags, setAvailableTags] = useState<AvailableTag[]>([])

  useEffect(() => {
    loadCostData()
    loadAvailableTags()
  }, [dateRange, appliedFilters])

  const loadCostData = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem('finops_token')
      const headers = { Authorization: `Bearer ${token}` }

      const startDate = format(dateRange.start, 'yyyy-MM-dd')
      const endDate = format(dateRange.end, 'yyyy-MM-dd')

      const params = new URLSearchParams()
      params.append('start', startDate)
      params.append('end', endDate)
      params.append('groupBy', appliedFilters.groupBy)

      if (appliedFilters.service) {
        params.append('service', appliedFilters.service)
      }

      if (Object.keys(appliedFilters.tags).length > 0) {
        params.append('tags', JSON.stringify(appliedFilters.tags))
      }

      const orgWideComparison =
        !appliedFilters.service && Object.keys(appliedFilters.tags).length === 0

      const requests = [
        axios.get(`/api/costs/cost-analysis?${params.toString()}`, { headers }),
        ...(orgWideComparison
          ? [axios.get(`/api/costs/summary?start=${startDate}&end=${endDate}`, { headers })]
          : [])
      ]
      const results = await Promise.all(requests)
      const response = results[0]!
      const summaryRes = orgWideComparison ? results[1] : null

      if (orgWideComparison && summaryRes?.data?.summary) {
        setPeriodChangePercent(summaryRes.data.summary.changePercent ?? 0)
      } else {
        setPeriodChangePercent(null)
      }

      if (response.data.breakdown) {
        const newTotal = response.data.total || 0
        setServiceCosts(response.data.breakdown)
        setTotalCost(newTotal)

        // Pass the total directly to avoid stale closure issue
        const hierarchical = buildHierarchicalData(
          response.data.breakdown,
          appliedFilters.groupBy,
          newTotal
        )
        setHierarchicalData(hierarchical)

        if (appliedFilters.groupBy === 'component' || appliedFilters.groupBy === 'resource') {
          const serviceAggregation = new Map<
            string,
            { service: string; cost: number; percentage: number }
          >()

          response.data.breakdown.forEach((item: any) => {
            if (serviceAggregation.has(item.service)) {
              const existing = serviceAggregation.get(item.service)!
              existing.cost += item.cost || 0
            } else {
              serviceAggregation.set(item.service, {
                service: item.service,
                cost: item.cost || 0,
                percentage: 0
              })
            }
          })

          const total = response.data.total || 0
          const aggregatedServices = Array.from(serviceAggregation.values()).map((service) => ({
            ...service,
            percentage: total > 0 ? (service.cost / total) * 100 : 0
          }))

          setServiceCosts(aggregatedServices)
        }

        const services = [
          ...new Set(response.data.breakdown.map((item: any) => item.service).filter(Boolean))
        ] as string[]
        setAvailableServices(services.sort())
      }
    } catch (error) {
      console.error('Failed to load cost data:', error)
      setServiceCosts([])
      setTotalCost(0)
      setHierarchicalData([])
    } finally {
      setLoading(false)
    }
  }

  const loadAvailableTags = async () => {
    try {
      const token = localStorage.getItem('finops_token')
      const headers = { Authorization: `Bearer ${token}` }

      const startDate = format(dateRange.start, 'yyyy-MM-dd')
      const endDate = format(dateRange.end, 'yyyy-MM-dd')

      const params = new URLSearchParams()
      params.append('start', startDate)
      params.append('end', endDate)

      if (appliedFilters.service) {
        params.append('service', appliedFilters.service)
      }

      const response = await axios.get(`/api/costs/available-tags?${params.toString()}`, {
        headers
      })

      if (response.data.availableTags) {
        setAvailableTags(response.data.availableTags)
      }
    } catch (error) {
      console.error('Failed to load available tags:', error)
      setAvailableTags([])
    }
  }

  const applyFilters = () => {
    setAppliedFilters({ ...pendingFilters })
  }

  const clearFilters = () => {
    const clearedFilters = { service: '', groupBy: 'service' as const, tags: {} }
    setPendingFilters(clearedFilters)
    setAppliedFilters(clearedFilters)
  }

  const hasPendingChanges = () => {
    return JSON.stringify(pendingFilters) !== JSON.stringify(appliedFilters)
  }

  const buildHierarchicalData = (
    data: any[],
    groupBy: string,
    total: number
  ): HierarchicalService[] => {
    const serviceMap = new Map<string, HierarchicalService>()
    // Use the passed total, not the stale state value
    const effectiveTotal = total > 0 ? total : 1

    if (data && Array.isArray(data)) {
      data.forEach((item: any) => {
        if (!serviceMap.has(item.service)) {
          serviceMap.set(item.service, {
            service: item.service,
            cost: 0,
            percentage: 0,
            components: []
          })
        }

        const serviceData = serviceMap.get(item.service)!

        if (groupBy === 'component') {
          const existingComponent = serviceData.components?.find(
            (c) => c.component === item.component
          )
          if (existingComponent) {
            existingComponent.cost += item.cost
          } else {
            serviceData.components?.push({
              component: item.component || 'Unknown Component',
              cost: item.cost,
              percentage: item.percentage || 0
            })
          }
        } else if (groupBy === 'resource') {
          let componentData = serviceData.components?.find((c) => c.component === item.component)
          if (!componentData) {
            componentData = {
              component: item.component || 'Unknown Component',
              cost: 0,
              percentage: 0,
              resources: []
            }
            serviceData.components?.push(componentData)
          }

          const existingResource = componentData.resources?.find(
            (r) => r.resource === item.resource
          )
          if (existingResource) {
            existingResource.cost += item.cost
          } else {
            componentData.resources?.push({
              resource: item.resource || 'Unknown Resource',
              cost: item.cost,
              percentage: item.percentage || 0
            })
          }
          componentData.cost += item.cost
        }

        serviceData.cost += item.cost
        serviceData.percentage = (serviceData.cost / effectiveTotal) * 100
      })

      serviceMap.forEach((serviceData) => {
        if (serviceData.components) {
          serviceData.components.forEach((component) => {
            component.percentage =
              serviceData.cost > 0 ? (component.cost / serviceData.cost) * 100 : 0

            if (component.resources) {
              component.resources.forEach((resource) => {
                resource.percentage =
                  component.cost > 0 ? (resource.cost / component.cost) * 100 : 0
              })
            }
          })
        }
      })
    }

    return Array.from(serviceMap.values()).sort((a, b) => b.cost - a.cost)
  }

  const toggleServiceExpansion = (service: string) => {
    const newExpanded = new Set(expandedServices)
    if (newExpanded.has(service)) {
      newExpanded.delete(service)
    } else {
      newExpanded.add(service)
    }
    setExpandedServices(newExpanded)
  }

  const toggleComponentExpansion = (componentKey: string) => {
    const newExpanded = new Set(expandedComponents)
    if (newExpanded.has(componentKey)) {
      newExpanded.delete(componentKey)
    } else {
      newExpanded.add(componentKey)
    }
    setExpandedComponents(newExpanded)
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(value)
  }

  const getDaysDifference = () => {
    const diffTime = Math.abs(dateRange.end.getTime() - dateRange.start.getTime())
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1
  }

  const filterZeroCostItems = <T extends { cost: number }>(items: T[]): T[] => {
    return items.filter((item) => item.cost > 0)
  }

  const filterHierarchicalData = (data: HierarchicalService[]): HierarchicalService[] => {
    return data
      .map((service) => ({
        ...service,
        components: service.components
          ? service.components
              .map((component) => ({
                ...component,
                resources: component.resources
                  ? filterZeroCostItems(component.resources)
                  : component.resources
              }))
              .filter((component) => component.cost > 0)
          : service.components
      }))
      .filter((service) => service.cost > 0)
  }

  const colors = ['#2563eb', '#059669', '#d97706', '#7c3aed', '#dc2626']

  const renderBreakdown = () => {
    const baseServiceCosts = appliedFilters.service
      ? serviceCosts.filter((item) => item.service === appliedFilters.service)
      : serviceCosts

    const baseHierarchicalData = appliedFilters.service
      ? hierarchicalData.filter((item) => item.service === appliedFilters.service)
      : hierarchicalData

    const filteredServiceCosts = filterZeroCostItems(baseServiceCosts)
    const filteredHierarchicalData = filterHierarchicalData(baseHierarchicalData)

    if (filteredServiceCosts.length === 0 && filteredHierarchicalData.length === 0) {
      return (
        <EmptyState
          icon="chart"
          title="No cost data available"
          description="Try adjusting your date range or filters."
          className="py-16"
        />
      )
    }

    if (appliedFilters.groupBy === 'service') {
      return filteredServiceCosts.slice(0, 15).map((item, index) => (
        <div
          key={item.service}
          className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 hover:shadow-md transition-shadow"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: colors[index % colors.length] }}
              />
              <div>
                <p className="font-medium text-gray-900">{item.service}</p>
                <p className="text-sm text-gray-500">
                  {item.percentage?.toFixed(1) || '0'}% of total
                </p>
              </div>
            </div>
            <p className="font-semibold text-gray-900">{formatCurrency(item.cost)}</p>
          </div>
        </div>
      ))
    }

    return filteredHierarchicalData.slice(0, 15).map((service, serviceIndex) => (
      <div
        key={service.service}
        className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden"
      >
        <div
          className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={() => toggleServiceExpansion(service.service)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <svg
                className={`w-4 h-4 text-gray-400 transition-transform ${expandedServices.has(service.service) ? 'rotate-90' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: colors[serviceIndex % colors.length] }}
              />
              <div>
                <p className="font-medium text-gray-900">{service.service}</p>
                <p className="text-sm text-gray-500">
                  {service.percentage?.toFixed(1) || '0'}% of total
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="font-semibold text-gray-900">{formatCurrency(service.cost)}</p>
              <p className="text-sm text-gray-500">{service.components?.length || 0} items</p>
            </div>
          </div>
        </div>

        {expandedServices.has(service.service) && service.components && (
          <div className="border-t border-gray-100 bg-gray-50">
            {service.components.map((component, componentIndex) => (
              <div key={`${service.service}-${component.component}`}>
                <div
                  className={`px-4 py-3 pl-12 ${
                    appliedFilters.groupBy === 'resource' && component.resources?.length
                      ? 'cursor-pointer hover:bg-gray-100'
                      : ''
                  } transition-colors`}
                  onClick={() => {
                    if (appliedFilters.groupBy === 'resource' && component.resources?.length) {
                      toggleComponentExpansion(`${service.service}-${component.component}`)
                    }
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {appliedFilters.groupBy === 'resource' && component.resources?.length ? (
                        <svg
                          className={`w-3 h-3 text-gray-400 transition-transform ${
                            expandedComponents.has(`${service.service}-${component.component}`)
                              ? 'rotate-90'
                              : ''
                          }`}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      ) : (
                        <div className="w-3" />
                      )}
                      <div
                        className="w-2 h-2 rounded-full"
                        style={{
                          backgroundColor: colors[(serviceIndex + componentIndex) % colors.length]
                        }}
                      />
                      <div>
                        <p className="font-medium text-gray-900 text-sm">{component.component}</p>
                        <p className="text-xs text-gray-500">
                          {component.percentage?.toFixed(1) || '0'}% of service
                        </p>
                      </div>
                    </div>
                    <p className="font-medium text-gray-900 text-sm">
                      {formatCurrency(component.cost)}
                    </p>
                  </div>
                </div>

                {appliedFilters.groupBy === 'resource' &&
                  expandedComponents.has(`${service.service}-${component.component}`) &&
                  component.resources && (
                    <div className="bg-white border-t border-gray-100">
                      {component.resources.map((resource) => (
                        <div
                          key={`${service.service}-${component.component}-${resource.resource}`}
                          className="px-4 py-2 pl-20 hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm text-gray-700">
                                {resource.resource.startsWith('Account-Level-')
                                  ? resource.resource.replace('Account-Level-', '')
                                  : resource.resource}
                              </p>
                              <p className="text-xs text-gray-400">
                                {resource.percentage?.toFixed(1) || '0'}%
                              </p>
                            </div>
                            <p className="text-sm font-medium text-gray-700">
                              {formatCurrency(resource.cost)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
              </div>
            ))}
          </div>
        )}
      </div>
    ))
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner"></div>
        <p className="ml-4 text-gray-600">Loading cost analysis...</p>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Cost Analysis</h1>
          <p className="mt-1 text-sm text-gray-600">Detailed AWS cost breakdown and trends</p>
        </div>
        <DateRangeSelector value={dateRange} onChange={setDateRange} />
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
              <svg
                className="w-6 h-6 text-blue-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Total Cost</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(totalCost)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="flex items-center gap-4">
            <div
              className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                periodChangePercent === null
                  ? 'bg-gray-100'
                  : periodChangePercent >= 0
                    ? 'bg-red-100'
                    : 'bg-green-100'
              }`}
            >
              <svg
                className={`w-6 h-6 ${
                  periodChangePercent === null
                    ? 'text-gray-500'
                    : periodChangePercent >= 0
                      ? 'text-red-600'
                      : 'text-green-600'
                }`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Change vs prior month</p>
              {periodChangePercent === null ? (
                <>
                  <p className="text-2xl font-bold text-gray-400">—</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Not available with service/tag filters
                  </p>
                </>
              ) : (
                <p
                  className={`text-2xl font-bold ${periodChangePercent >= 0 ? 'text-red-600' : 'text-green-600'}`}
                >
                  {periodChangePercent >= 0 ? '+' : ''}
                  {periodChangePercent.toFixed(1)}%
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
              <svg
                className="w-6 h-6 text-amber-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Daily Average</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(totalCost / getDaysDifference())}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center">
              <svg
                className="w-6 h-6 text-gray-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Services</p>
              <p className="text-2xl font-bold text-gray-900">
                {filterZeroCostItems(serviceCosts).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          <div className="flex items-center gap-2 flex-shrink-0">
            <svg
              className="w-5 h-5 text-gray-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
              />
            </svg>
            <span className="font-medium text-gray-700">Filters</span>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 flex-1">
            <select
              value={pendingFilters.service}
              onChange={(e) => setPendingFilters((prev) => ({ ...prev, service: e.target.value }))}
              className="form-input"
            >
              <option value="">All Services</option>
              {availableServices.map((service) => (
                <option key={service} value={service}>
                  {getServiceAbbreviation(service)}
                </option>
              ))}
            </select>

            <select
              value={pendingFilters.groupBy}
              onChange={(e) =>
                setPendingFilters((prev) => ({ ...prev, groupBy: e.target.value as any }))
              }
              className="form-input"
            >
              <option value="service">By Service</option>
              <option value="component">By Component</option>
              <option value="resource">By Resource</option>
            </select>

            <select
              value={Object.keys(pendingFilters.tags)[0] || ''}
              onChange={(e) => {
                if (e.target.value) {
                  const selectedTag = availableTags.find((tag) => tag.key === e.target.value)
                  if (selectedTag && selectedTag.values.length > 0) {
                    setPendingFilters((prev) => ({
                      ...prev,
                      tags: { [e.target.value]: selectedTag.values[0] || '' }
                    }))
                  }
                } else {
                  setPendingFilters((prev) => ({ ...prev, tags: {} }))
                }
              }}
              className="form-input"
            >
              <option value="">All Tags</option>
              {availableTags.map((tag) => (
                <option key={tag.key} value={tag.key}>
                  {tag.key} ({tag.count})
                </option>
              ))}
            </select>

            {Object.keys(pendingFilters.tags).length > 0 && (
              <select
                value={Object.values(pendingFilters.tags)[0] || ''}
                onChange={(e) => {
                  const tagKey = Object.keys(pendingFilters.tags)[0]
                  if (tagKey && e.target.value) {
                    setPendingFilters((prev) => ({ ...prev, tags: { [tagKey]: e.target.value } }))
                  } else if (tagKey) {
                    setPendingFilters((prev) => ({ ...prev, tags: {} }))
                  }
                }}
                className="form-input"
              >
                <option value="">Select Value</option>
                {(() => {
                  const tagKey = Object.keys(pendingFilters.tags)[0]
                  const tag = availableTags.find((t) => t.key === tagKey)
                  return tag
                    ? tag.values.map((value) => (
                        <option key={value} value={value}>
                          {value}
                        </option>
                      ))
                    : null
                })()}
              </select>
            )}
          </div>

          <div className="flex gap-2 flex-shrink-0">
            <button
              onClick={applyFilters}
              disabled={!hasPendingChanges()}
              className={`btn ${hasPendingChanges() ? 'btn-primary' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}
            >
              Apply
            </button>
            <button onClick={clearFilters} className="btn btn-secondary">
              Clear
            </button>
          </div>
        </div>

        {(appliedFilters.service || Object.keys(appliedFilters.tags).length > 0) && (
          <div className="mt-4 flex flex-wrap gap-2">
            {appliedFilters.service && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                Service: {getServiceAbbreviation(appliedFilters.service)}
                <button onClick={() => clearFilters()} className="ml-2 hover:text-blue-900">
                  ×
                </button>
              </span>
            )}
            {Object.entries(appliedFilters.tags).map(([key, value]) => (
              <span
                key={key}
                className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800"
              >
                {key}: {value}
                <button
                  onClick={() => {
                    const newTags = { ...appliedFilters.tags }
                    delete newTags[key]
                    setAppliedFilters((prev) => ({ ...prev, tags: newTags }))
                    setPendingFilters((prev) => ({ ...prev, tags: newTags }))
                  }}
                  className="ml-2 hover:text-green-900"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Cost Breakdown */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          {appliedFilters.groupBy === 'service'
            ? 'Service Cost Breakdown'
            : appliedFilters.groupBy === 'component'
              ? 'Service → Component Breakdown'
              : 'Service → Component → Resource Breakdown'}
        </h2>
        <div className="space-y-3">{renderBreakdown()}</div>
      </div>
    </div>
  )
}
